import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { useAuth } from "../contexts/AuthContext";
import RoleBasedLayout, {
  type LayoutMenuItem,
} from "../components/RoleBasedLayout";
import {
  LayoutDashboard,
  Building,
  BookOpen,
  BarChart3,
  DollarSign,
  FileText,
  Users,
  LayoutGrid,
  UserCircle,
  Megaphone,
  Headphones,
  Mail,
  Truck,
  MapPin,
  GraduationCap,
  PlayCircle,
  Calendar,
  Layers,
  Image,
  Newspaper,
  HelpCircle,
  MessageSquare,
  Package,
  Globe,
  Map,
  Building2,
  Locate,
  Shield,
  Smartphone,
  MailOpen,
  UserCog,
  Briefcase,
} from "lucide-react";
import Dashboard from "./super-admin/Dashboard";
import Hostels from "./super-admin/Hostels";
import Institutions from "./super-admin/Institutions";
import InstitutionDetail from "./super-admin/InstitutionDetail";
import AddInstitution from "./super-admin/AddInstitution";
import Scholarships from "./super-admin/Scholarships";
import InstitutionReport from "./super-admin/reports/InstitutionReport";
import DonationReport from "./super-admin/reports/DonationReport";
import DonationReports from "./super-admin/DonationReports";
import Funding from "./super-admin/Funding";
import UserManagement from "./super-admin/UserManagement";
import Crm from "./super-admin/CRM";
import ContactQueries from "./super-admin/ContactQueries";
import Podcasts from "./super-admin/cms/Podcasts";
import Events from "./super-admin/cms/Events";
import Blogs from "./super-admin/cms/Blogs";
import MediaGallery from "./super-admin/cms/MediaGallery";
import PressCoverage from "./super-admin/cms/PressCoverage";
import Vendors from "./super-admin/Vendors";
import CategoriesSubCategories from "./super-admin/CategoriesSubCategories";
import ListMastersIndex from "./super-admin/list-masters/index";
import CountryPage from "./super-admin/list-masters/Country";
import ZonePage from "./super-admin/list-masters/Zone";
import StatePage from "./super-admin/list-masters/State";
import CityPage from "./super-admin/list-masters/City";
import DistrictPage from "./super-admin/list-masters/District";
import AreaPage from "./super-admin/list-masters/Area";
import FacilitiesPage from "./super-admin/list-masters/Facilities";
import DesignationPage from "./super-admin/list-masters/Designation";
import DonationMasterPage from "./super-admin/list-masters/DonationMaster";
import DonationMastersPage from "./super-admin/list-masters/DonationMasters";
import DonationCampaignsPage from "./super-admin/DonationCampaigns";
import FAQManagement from "./super-admin/FAQManagement";
import TestimonialsManagement from "./super-admin/TestimonialsManagement";
import StudentInquiries from "./super-admin/StudentInquiries";
import RolesPermissions from "./super-admin/RolesPermissions";
import SmsLogs from "./super-admin/SmsLogs";
import EmailLogs from "./super-admin/EmailLogs";
import ProgramsInitiatives from "./super-admin/ProgramsInitiatives";

const superAdminMenuItems: LayoutMenuItem[] = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/super-admin/dashboard",
    superAdminOnly: true,
  },
  {
    icon: Building,
    label: "Institutions",
    path: "/super-admin/institutions",
    subItems: [
      {
        label: "Institutions",
        path: "/super-admin/institutions",
        icon: Building,
      },
      {
        label: "Facilities",
        path: "/super-admin/list-masters/facilities-master",
        icon: Building2,
      },
      {
        label: "Designation Master",
        path: "/super-admin/list-masters/designation-master",
        icon: Users,
      },
    ],
  },
  {
    icon: GraduationCap,
    label: "Student Inquiries",
    path: "/super-admin/student-inquiries",
    superAdminOnly: true,
  },
  {
    icon: Truck,
    label: "Vendors",
    path: "/super-admin/vendors",
    subItems: [
      {
        label: "Vendors Management",
        path: "/super-admin/vendors",
        icon: Truck,
      },
      { label: "Categories", path: "/super-admin/categories", icon: Package },
    ],
  },
  {
    icon: MapPin,
    label: "List Masters",
    path: "/super-admin/list-masters",
    subItems: [
      {
        label: "Country",
        path: "/super-admin/list-masters/country",
        icon: Globe,
      },
      { label: "Zone", path: "/super-admin/list-masters/zone", icon: Layers },
      { label: "State", path: "/super-admin/list-masters/state", icon: Map },
      {
        label: "District",
        path: "/super-admin/list-masters/district",
        icon: Locate,
      },
      {
        label: "City",
        path: "/super-admin/list-masters/city",
        icon: Building2,
      },
      { label: "Area", path: "/super-admin/list-masters/area", icon: Locate },
      // {
      //   label: "Facilities",
      //   path: "/super-admin/list-masters/facilities-master",
      //   icon: Building,
      // },
    ],
  },
  { icon: BookOpen, label: "Scholarships", path: "/super-admin/scholarships" },
  {
    icon: Briefcase,
    label: "Programs & Initiatives",
    path: "/super-admin/programs-initiatives",
  },
  {
    icon: BarChart3,
    label: "Reports",
    path: "/super-admin/reports/institution",
    subItems: [
      {
        label: "Institution Report",
        path: "/super-admin/reports/institution",
        icon: Building,
      },
      {
        label: "Donation Report",
        path: "/super-admin/reports/donation",
        icon: DollarSign,
      },
    ],
  },
  {
    icon: DollarSign,
    label: "Donation",
    path: "/super-admin/funding",
    subItems: [
      {
        label: "Donation",
        path: "/super-admin/funding",
        icon: DollarSign,
      },
      {
        label: "Donor List",
        path: "/super-admin/donation-master",
        icon: UserCircle,
      },
      {
        label: "Donation Masters",
        path: "/super-admin/donation-masters",
        icon: LayoutGrid,
      },
      {
        label: "Donation Campaigns",
        path: "/super-admin/donation-campaigns",
        icon: Megaphone,
      },
      {
        label: "Donation Reports",
        path: "/super-admin/donation-reports",
        icon: BarChart3,
      },
    ],
  },
  {
    icon: FileText,
    label: "CMS Pages",
    path: "/super-admin/cms",
    subItems: [
      {
        label: "Podcasts",
        path: "/super-admin/cms/podcasts",
        icon: PlayCircle,
      },
      { label: "Events", path: "/super-admin/cms/events", icon: Calendar },
      { label: "Blogs", path: "/super-admin/cms/blogs", icon: Layers },
      { label: "Media Gallery", path: "/super-admin/cms/media", icon: Image },
      {
        label: "Press Coverage",
        path: "/super-admin/cms/press",
        icon: Newspaper,
      },
      { label: "FAQ Management", path: "/super-admin/faq", icon: HelpCircle },
      {
        label: "Testimonials",
        path: "/super-admin/testimonials",
        icon: MessageSquare,
      },
    ],
  },
  {
    icon: UserCog,
    label: "User Management",
    path: "/super-admin/user-management",
    superAdminOnly: true,
    subItems: [
      {
        label: "Users",
        path: "/super-admin/user-management",
        icon: Users,
      },
      {
        label: "Roles & Permissions",
        path: "/super-admin/roles-permissions",
        icon: Shield,
      },
    ],
  },
  { icon: Headphones, label: "CRM Module", path: "/super-admin/crm" },
  {
    icon: Mail,
    label: "Contact Us",
    path: "/super-admin/contact",
    superAdminOnly: true,
  },
  {
    icon: Users,
    label: "Logs",
    path: "/super-admin/sms-logs",
    superAdminOnly: true,
    subItems: [
      {
        label: "SMS Logs",
        path: "/super-admin/sms-logs",
        icon: Smartphone,
      },
      {
        label: "Email Logs",
        path: "/super-admin/email-logs",
        icon: MailOpen,
      },
    ],
  },
];

export default function SuperAdminDashboard() {
  const { userRole, loading } = useAuth();
  const navigate = usePrefixedNavigate();

  const isSuperAdmin = userRole === "super_admin";
  const menuItems = superAdminMenuItems.filter(
    item => !item.superAdminOnly || isSuperAdmin
  );

  useEffect(() => {
    // Only redirect if loading is complete and role doesn't match
    if (import.meta.env.DEV) {
      console.log("SuperAdminDashboard Auth State:", { loading, userRole });
    }
    if (
      !loading &&
      userRole &&
      userRole !== "super_admin" &&
      userRole !== "management"
    ) {
      if (import.meta.env.DEV) {
        console.warn(
          "Redirecting to portal-selection due to mismatching role:",
          userRole
        );
      }
      navigate("/jainboarding-admin/auth?portal=super_admin");
    }
  }, [userRole, loading, navigate]);

  return (
    <RoleBasedLayout
      menuItems={menuItems}
      sidebarTitle={isSuperAdmin ? "Super Admin" : "Management"}
      sidebarSubtitle="Control Panel"
    >
      <Routes>
        {/* Routes are relative to /super-admin/* parent route */}
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/hostels" element={<Hostels />} />
        <Route path="/institutions" element={<Institutions />} />
        <Route path="/student-inquiries" element={<StudentInquiries />} />
        <Route path="/institutions/:id" element={<InstitutionDetail />} />
        <Route path="/institutions/add" element={<AddInstitution />} />
        <Route path="/scholarships" element={<Scholarships />} />
        <Route path="/programs-initiatives" element={<ProgramsInitiatives />} />
        <Route
          path="/reports"
          element={<Navigate to="institution" replace />}
        />
        <Route path="/reports/institution" element={<InstitutionReport />} />
        <Route path="/reports/donation" element={<DonationReport />} />
        <Route path="/funding" element={<Funding />} />
        <Route path="/donation-reports" element={<DonationReports />} />
        <Route path="/donation-master" element={<DonationMasterPage />} />
        <Route path="/donation-masters" element={<DonationMastersPage />} />
        <Route
          path="/donor-type-master"
          element={<Navigate to="donation-masters?tab=donor-type" replace />}
        />
        <Route
          path="/donor-category-master"
          element={
            <Navigate to="donation-masters?tab=donor-category" replace />
          }
        />
        <Route
          path="/donation-type-master"
          element={<Navigate to="donation-masters?tab=donation-type" replace />}
        />
        <Route
          path="/donation-main-category-master"
          element={
            <Navigate
              to="donation-masters?tab=donation-main-category"
              replace
            />
          }
        />
        <Route
          path="/donation-sub-category-master"
          element={
            <Navigate to="donation-masters?tab=donation-sub-category" replace />
          }
        />
        <Route path="/donation-campaigns" element={<DonationCampaignsPage />} />
        <Route path="/user-management" element={<UserManagement />} />
        <Route path="/roles-permissions" element={<RolesPermissions />} />
        <Route path="/crm" element={<Crm />} />
        <Route path="/contact" element={<ContactQueries />} />
        <Route path="/sms-logs" element={<SmsLogs />} />
        <Route path="/email-logs" element={<EmailLogs />} />
        <Route path="/vendors" element={<Vendors />} />
        <Route path="/categories" element={<CategoriesSubCategories />} />
        <Route path="/faq" element={<FAQManagement />} />
        <Route path="/testimonials" element={<TestimonialsManagement />} />
        <Route path="/list-masters" element={<ListMastersIndex />} />
        <Route path="/list-masters/country" element={<CountryPage />} />
        <Route path="/list-masters/zone" element={<ZonePage />} />
        <Route path="/list-masters/state" element={<StatePage />} />
        <Route path="/list-masters/city" element={<CityPage />} />
        <Route path="/list-masters/district" element={<DistrictPage />} />
        <Route path="/list-masters/area" element={<AreaPage />} />
        <Route
          path="/list-masters/facilities-master"
          element={<FacilitiesPage />}
        />
        <Route
          path="/list-masters/designation-master"
          element={<DesignationPage />}
        />
        <Route path="/cms/podcasts" element={<Podcasts />} />
        <Route path="/cms/events" element={<Events />} />
        <Route path="/cms/blogs" element={<Blogs />} />
        <Route path="/cms/media" element={<MediaGallery />} />
        <Route path="/cms/press" element={<PressCoverage />} />
      </Routes>
    </RoleBasedLayout>
  );
}
